import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import {
  Download,
  Eye,
  Lock,
  RefreshCw,
  MoreHorizontal,
  // DollarSign,
} from "lucide-react";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";
import { AttendanceService } from "@/services/Attendance/AttendanceService";
import { ThrowError } from "@/api/callApi";
import type { IAttendanceSummaryUser } from "@/interface/attendance.interface";
import type { IPageInfo, IFilter } from "@/interface/common.interface";
import TablePagination from "@/components/common/TablePagination";
import CommonFilter from "@/components/common/CommonFilter";
import moment from "moment";
import { useAppFilters } from "@/hooks/useAppFilters";

const MonthlyTeamAttendance: React.FC = () => {
  const navigate = useNavigate();

  // ── Universal App Filters ──────────────────────────────────────────────────
  const {
    isHQ,
    effectiveBranchId,
    effectiveSiteId,
    filterValues,
    queryString: filterQueryString,
    filterSelectorsNode,
  } = useAppFilters({
    enabled: ["branch", "site", "department"],
    autoSelectFirst: { branch: true, site: true },
    onChange: () => {
      setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    },
  });

  const [isExporting, setIsExporting] = useState(false);

  const getInitialDates = () => {
    const today = moment();
    if (today.date() >= 21) {
      return {
        startDate: today.clone().date(21).format("YYYY-MM-DD"),
        endDate: today.clone().add(1, "month").date(20).format("YYYY-MM-DD"),
      };
    }
    return {
      startDate: today.clone().subtract(1, "month").date(21).format("YYYY-MM-DD"),
      endDate: today.clone().date(20).format("YYYY-MM-DD"),
    };
  };

  const initialDates = getInitialDates();

  const [filter, setFilter] = useState<IFilter>({
    search: "",
    startDate: initialDates.startDate,
    endDate: initialDates.endDate,
  });

  const handleDateRangeChange = useCallback((start: string, end: string) => {
    setFilter((prev) => ({ ...prev, startDate: start, endDate: end }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleSearchChange = (value: string) => {
    setFilter((prev) => {
      if (prev.search === value) return prev;
      return { ...prev, search: value };
    });
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const [attendanceData, setAttendanceData] = useState<
    IAttendanceSummaryUser[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const searchParam = filter.search
        ? `&search=${encodeURIComponent(filter.search)}`
        : "";

      const totalRecords = paginationData.pageSize || 1000;
      const params = `download=true&startDate=${filter.startDate}&endDate=${filter.endDate}&pageNumber=${paginationData.currentPage}&pageSize=${totalRecords}${filterQueryString}${searchParam}`;

      const blob = await AttendanceService.downloadMonthlyAttendanceReport(params);
      console?.log("blob", blob);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `monthly_attendance_${filter.startDate}_to_${filter.endDate}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleGetMonthlyAttendance = async (
    page = paginationData.currentPage,
    limit = paginationData.pageSize
  ) => {
    // If HQ and no branch/site selected yet, skip until loaded
    if (isHQ && !effectiveBranchId && !effectiveSiteId) return;

    setIsLoading(true);
    try {
      const searchParam = filter.search
        ? `&search=${encodeURIComponent(filter.search)}`
        : "";

      const params = `type=monthly&startDate=${filter.startDate}&endDate=${filter.endDate}&pageNumber=${page}&pageSize=${limit}${filterQueryString}${searchParam}`;
      const response = await AttendanceService.getDailyTeamAttendance(params);
      if (response?.status && response?.data) {
        setAttendanceData(response.data.users || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response?.data?.pageInfo?.total || 0,
          totalPages: response?.data?.pageInfo?.totalPages || 0,
          currentPage: page,
          pageSize: limit,
        }));
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  useEffect(() => {
    handleGetMonthlyAttendance(
      paginationData.currentPage,
      paginationData.pageSize
    );
  }, [
    paginationData.currentPage,
    paginationData.pageSize,
    filter.startDate,
    filter.endDate,
    filter.search,
    effectiveBranchId,
    effectiveSiteId,
    filterValues.departmentId,
  ]);

  const columns: TableColumn<IAttendanceSummaryUser>[] = [
    {
      key: "sNo",
      header: "S.No",
      render: (_item: IAttendanceSummaryUser, index: number) => (
        <span className="text-muted-foreground font-medium text-sm">
          {String(
            (paginationData.currentPage - 1) * paginationData.pageSize +
            index +
            1
          ).padStart(2, "0")}
        </span>
      ),
    },
    {
      key: "empid",
      header: "Emp ID",
      render: (item) => (
        <span className="font-semibold text-slate-700">
          {item.user?.employeeCode || "—"}
        </span>
      ),
    },
    {
      key: "name",
      header: "Name",
      render: (item) => (
        <span onClick={() => navigate(`${RoutesLink.employeeDetail}/${item?.user?.id}`)} className="font-bold text-slate-900 cursor-pointer">
          {item.user?.name || "—"}
        </span>
      ),
    },
    {
      key: "department",
      header: "Department",
      render: (item) => <span>{item?.department || "—"}</span>,
    },
    {
      key: "shift",
      header: "Shift",
      render: (item) => <span>{item?.shift || "—"}</span>,
    },
    {
      key: "present",
      header: "Present",
      render: (item) => (
        <span className="text-green-600 font-medium">
          {item.summary.present !== undefined ? item.summary.present : 0}
        </span>
      ),
    },
    {
      key: "pending",
      header: "Pending",
      render: (item) => (
        <span className="text-red-600 font-medium">
          {item.summary.pending !== undefined ? item.summary.pending : 0}
        </span>
      ),
    },
    {
      key: "wfh",
      header: "WFH",
      render: (item) => (
        <span className="text-green-600 font-medium">
          {item.summary.wfh !== undefined ? item.summary.wfh : 0}
        </span>
      ),
    },
    {
      key: "pendingWfh",
      header: "Pending-WFH",
      render: (item) => (
        <span className="text-red-600 font-medium">
          {item.summary.pendingWfh !== undefined ? item.summary.pendingWfh : 0}
        </span>
      ),
    },
    {
      key: "pendingAr",
      header: "Pending-Ar",
      render: (item) => (
        <span className="text-red-600 font-medium">
          {item.summary.pendingAr !== undefined ? item.summary.pendingAr : 0}
        </span>
      ),
    },
    {
      key: "absent",
      header: "Absent",
      render: (item) => (
        <span className="text-red-600 font-medium">
          {item.summary?.absent !== undefined ? item.summary?.absent : 0}
        </span>
      ),
    },
    {
      key: "leave",
      header: "Leave",
      render: (item) => (
        <span className="text-amber-600 font-medium">
          {item.summary?.leaves !== undefined ? item.summary?.leaves : 0}
        </span>
      ),
    },
    {
      key: "pendingLeave",
      header: "Pending-Leave",
      render: (item) => (
        <span className="text-amber-600 font-medium">
          {item.summary?.pendingLeave !== undefined
            ? item.summary?.pendingLeave
            : 0}
        </span>
      ),
    },
    {
      key: "pendingOutdoorDuty",
      header: "Pending-OD",
      render: (item) => (
        <span className="text-amber-600 font-medium">
          {item.summary?.pendingOutdoorDuty !== undefined
            ? item.summary?.pendingOutdoorDuty
            : 0}
        </span>
      ),
    },
    {
      key: "weekOff",
      header: "WeekOff",
      render: (item) => (
        <span className="text-amber-600 font-medium">
          {item.summary?.weekOff !== undefined ? item.summary?.weekOff : 0}
        </span>
      ),
    },
    {
      key: "holiday",
      header: "Holiday",
      render: (item) => (
        <span className="text-amber-600 font-medium">
          {item.summary?.holiday !== undefined ? item.summary?.holiday : 0}
        </span>
      ),
    },
    {
      key: "lateMarks",
      header: "Late Marks",
      render: (item) => (
        <span className="text-red-500 font-medium">
          {item.summary?.pendingAr !== undefined ? item.summary?.pendingAr : 0}
        </span>
      ),
    },
    {
      key: "totalDays",
      header: "Total Days",
      render: (item) => (
        <span>{item?.totalDays !== undefined ? item?.totalDays : 0}</span>
      ),
    },
    // NEW COLUMN: Lock Status
    {
      key: "lockStatus",
      header: "Lock Status",
      render: (item) => {
        const isLocked = item.attendanceLock?.isLocked;
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${isLocked ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
            {isLocked ? (
              <span className="flex items-center gap-1">
                <Lock size={14} /> Locked
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Lock size={14} className="opacity-50" /> Unlocked
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-slate-100">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4 text-slate-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="rounded-xl shadow-lg border-slate-100"
          >
            <DropdownMenuItem
              onClick={() => {
                if (item.user?.id) {
                  // const date = moment(filter.startDate, "YYYY-MM-DD").format(
                  //   "MM-YYYY"
                  // );
                  navigate(
                    `${RoutesLink.monthlyTeamAttendanceSummary}/${item.user.id}`,
                    { state: { startDate: filter.startDate, endDate: filter.endDate } }
                  );
                }
              }}
              className="cursor-pointer"
            >
              <Eye className="h-4 w-4" />
              View Logs
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-wrap">
          <MainHeading title="Monthly Team Attendance" isBackButtonVisible={true} />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="bg-white"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <RefreshCw size={16} className="mr-2 animate-spin" />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Export
            </Button>

          </div>
        </div>

        <CommonFilter
          startDate={filter.startDate || ""}
          endDate={filter.endDate || ""}
          onDateRangeChange={handleDateRangeChange}
          searchValue={filter.search || ""}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search employees..."
          value={filter}
          additionalFilters={filterSelectorsNode}
        />

        <CommonTable
          columns={columns}
          data={attendanceData}
          loading={isLoading}
          stickyColumns={3}
        />

        <TablePagination
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          handleChangePageSize={handleChangePageSize}
          isLoading={isLoading}
        />
      </div>
    </Layout>
  );
};

export default MonthlyTeamAttendance;